import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Paper, TextField, Typography } from '@mui/material';

import { Radio, RadioLabel, Separator } from '../../../components/common';
import {
  CANCELLATION_REASON_OTHER,
  CHANGE_CANCELATION_REASON,
  GET_CONTRACT_CANCELLATION_REASONS,
} from '../../../shared';
import {
  StyledImageContainer,
  StyledCancellationReasonsTitle,
  StyledCancellationReasonsDescription,
  StyledButtonContainer,
  StyledReason,
  StyledOtherReasonCommentContainer,
} from './CancellationReasons.styled';

export default function CancellationReasons({
  pageTitleId,
  pageDescriptionId,
  pageIcon,
  pageIconAlt,
}) {
  const history = useHistory();
  const intl = useIntl();

  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');

  const { data } = useQuery(GET_CONTRACT_CANCELLATION_REASONS, {
    fetchPolicy: 'network-only',
  });
  const cancellationReasons = data?.cancellationAvailableReasons?.reasons;

  const [changeCancelationReason] = useMutation(CHANGE_CANCELATION_REASON, {
    variables: {
      cancelationReason: reason,
      comment: reason === CANCELLATION_REASON_OTHER ? comment : '',
    },
  });

  const handleButtonClick = () => {
    if (reason) {
      changeCancelationReason().then(() => {
        history.push('/contract-cancellation/success');
      });
    }
  };

  const disableButton = !reason || (reason === CANCELLATION_REASON_OTHER && !comment);

  return (
    <Paper>
      {pageIcon && (
        <StyledImageContainer>
          <img src={pageIcon} alt={pageIconAlt} />
        </StyledImageContainer>
      )}

      <StyledCancellationReasonsTitle>
        <Typography variant="h3">
          <FormattedMessage id={pageTitleId} />
        </Typography>
      </StyledCancellationReasonsTitle>

      <Separator />

      <StyledCancellationReasonsDescription>
        <Typography variant="p">
          <FormattedMessage id={pageDescriptionId} />
        </Typography>
      </StyledCancellationReasonsDescription>

      {cancellationReasons &&
        Object.keys(cancellationReasons).map((item) => (
          <StyledReason key={item}>
            <Radio
              name="reason"
              onChange={(e) => {
                e.stopPropagation();
                setReason(item);
              }}
              value={item}
              checked={item === reason}
            />
            <RadioLabel onClick={() => setReason(item)}>
              <Typography variant="p" color="professional_blue">
                {cancellationReasons[`${item}`]}
              </Typography>
            </RadioLabel>
          </StyledReason>
        ))}

      {reason === CANCELLATION_REASON_OTHER && (
        <StyledOtherReasonCommentContainer>
          <TextField
            multiline
            fullWidth
            rows={4}
            required
            id="other-cancellation-reason"
            name="other-cancellation-reason"
            label={
              <Typography variant="h3" component="span">
                <FormattedMessage id="cancellation.contract.reason.other.comment.label" />
              </Typography>
            }
            placeholder={intl.formatMessage({
              id: 'cancellation.contract.reason.other.comment.placeholder',
            })}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </StyledOtherReasonCommentContainer>
      )}

      <Separator />

      <StyledButtonContainer>
        <Button variant="outlined" onClick={() => history.push('/')}>
          <Typography variant="buttons">
            <FormattedMessage id="cancellation.contract.button.backToHome" />
          </Typography>
        </Button>

        <Button disabled={disableButton} onClick={() => handleButtonClick()}>
          <Typography variant="buttons">
            <FormattedMessage id="cancellation.contract.button.send" />
          </Typography>
        </Button>
      </StyledButtonContainer>
    </Paper>
  );
}
